
import { Slide } from '@hokify/hooper';
import type { IAPICompany, IAPICompanyMedia, IAPIJobField, IAPILocation } from '@hokify/common';
import type { PropType } from 'vue';
import { CompanySwipeType } from '@hokify/common';
import { defineComponent } from 'vue';
import { piniaAvailable } from '../helpers/piniaStoreAvailable';
import BeautifyText from './BeautifyText.vue';
import { truncateFct } from '../helpers/truncate';
import Location from './Company/Location.vue';
import Fields from './Company/Fields.vue';
import HokBgImg from './HokBgImg.vue';

function isHTMLElement(element: any): element is HTMLElement {
	return element && element instanceof HTMLElement;
}

interface IAdditionalData {
	jobCount: number;
	useBodyContainer: boolean;
}

type MapState = 'image' | 'map';

export default defineComponent({
	name: 'CompanyPage',
	components: {
		Fields,
		BeautifyText,
		HokCarousel: () => import('./HokCarousel.vue'),
		HokMarkerMap: () => import('./HokMarkerMap.vue'),
		Pagination: () => import('./Pagination.vue'),
		Slide,
		Location,
		HokBgImg
	},
	beforeDestroy() {
		this.observer?.disconnect();
	},
	methods: {
		truncateFct,
		setMapState(state: MapState) {
			this.mapState = state;
		},
		toAnchor(e, anchor: string) {
			e.preventDefault();

			if (this.editMode && anchor === 'initiative') {
				this.$emit('switch-to-applications');
			}
			const element = this.$refs[anchor];
			if (isHTMLElement(element)) {
				element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		},
		updateCurrentLocationPage(direction: 'next' | 'prev') {
			if (direction === 'next') {
				this.currentLocationPage += 1;
			} else {
				this.currentLocationPage -= 1;
			}
		},
		registerIntersectionObserver() {
			const navigationSections = document.querySelectorAll(
				`#about-us,#contact-person, #jobs, #locations, #start `
			);
			const navMenu = document.querySelectorAll('#nav-menu li');

			const options = {
				rootMargin: '-10% 0px -90% 0px',
				threshhold: 0.5
			};

			this.observer = new IntersectionObserver(entries => {
				entries.forEach(e => {
					if (e.isIntersecting) {
						navMenu.forEach(item => {
							const navItemName = item.id.replace('nav-', '');
							item.classList.remove('font-bold');
							if (e.target.id === navItemName) {
								item.classList.add('font-bold');
							}
						});
					}
				});
			}, options);

			navigationSections.forEach(section => {
				this.observer?.observe(section);
			});
		}
	},
	data() {
		const observer = undefined as undefined | IntersectionObserver;
		const mapState = 'image' as MapState;

		return {
			mapState,
			showFullText: false,
			showAllFields: false,
			currentLocationPage: 1,
			maxSecondaryLocations: this.$isMobile.phone ? 4 : 2,
			observer,
			showFullSlogan: false,
			piniaAvailable
		};
	},
	computed: {
		topLevelDomain(): string {
			return piniaAvailable(this.$nuxt, '$userRootStore')
				? this.$nuxt.context.$userRootStore?.topLevelDomain
				: this.$store.state.topLevelDomain || 'at';
		},
		visibleFields(): IAPIJobField[] | undefined {
			return this.showAllFields ? this.company?.fields : this.company?.fields?.slice(0, 5);
		},
		coverImages() {
			return (
				// eslint-disable-next-line global-require
				this.company?.coverImage || require('../../assets/images/company-placeholder-header.png')
			);
		},
		mediaCount(): number {
			return this.media?.length || 0;
		},
		media(): IAPICompanyMedia[] | undefined {
			return this.company?.about?.media;
		},
		locationCount(): number | undefined {
			return this.company?.location?.length;
		},
		jobFieldCount(): number | undefined {
			return this.company?.fields?.length;
		},
		jobCount(): number | undefined {
			return this.additionalData?.jobCount;
		},
		primaryLocation(): IAPILocation | undefined {
			return this.company?.location?.[0];
		},
		secondaryLocations(): IAPILocation[] | undefined {
			return this.company?.location?.slice(1); // returns all items from and including index 1
		},
		isCompanySwipeActionDiscover(): boolean {
			return this.company?.swipeAction === CompanySwipeType.Discover;
		},
		visibleSecondaryLocations(): IAPILocation[] | undefined {
			if (
				this.secondaryLocations?.length &&
				this.secondaryLocations.length <= this.maxSecondaryLocations
			) {
				return this.secondaryLocations;
			}
			return this.secondaryLocations?.slice(
				0 + this.maxSecondaryLocations * (this.currentLocationPage - 1),
				this.maxSecondaryLocations + this.maxSecondaryLocations * (this.currentLocationPage - 1)
				// e.g. (0, 2), (2, 4), etc
			);
		},
		contactPersonHeadline(): string {
			if (this.visibleTrustedUsers && this.visibleTrustedUsers.length > 1) {
				return 'Deine Ansprechpartner';
			}
			return this.visibleTrustedUsers?.[0]?.general?.gender === 'female'
				? 'Deine Ansprechpartnerin'
				: 'Dein Ansprechpartner';
		},
		hasTrustedUsers(): boolean {
			return !!this.company?.organization?.owners?.length;
		},
		visibleTrustedUsers(): any[] | undefined {
			if (this.availableTrustedUsers < 1) return undefined;
			return this.company?.organization?.owners;
		},
		availableTrustedUsers(): number {
			return this.company?.organization?.owners?.length || 0;
		},
		getLocationText(): string {
			if (this.primaryLocation?.name && this.primaryLocation?.name.length < 40) {
				return this.primaryLocation?.name;
			}
			if (this.primaryLocation?.district && this.primaryLocation?.country) {
				return `${this.primaryLocation?.district}, ${this.primaryLocation?.country}`;
			}
			if (this.primaryLocation?.city && this.primaryLocation?.country) {
				return `${this.primaryLocation?.city}, ${this.primaryLocation?.country}`;
			}
			return this.primaryLocation?.name || '';
		},
		companyClaimMail(): string {
			const title = `Unternehmens-Page bearbeiten - https://hokify.${this.topLevelDomain}/c/${this.company.alias}`;
			return encodeURI(`mailto:info@hokify.at?subject=${title}`);
		},
		enoughDataForSplitTiles(): boolean {
			// count if there is enough data to support 3 tiles
			return (
				[
					this.locationCount,
					this.jobFieldCount,
					this.jobCount,
					this.company?.allowUnsolicitedApplications
				].filter(truthy => truthy).length >= 2
			);
		},
		locationPageCount(): number {
			if (this.secondaryLocations?.length) {
				return Math.ceil(this.secondaryLocations.length / this.maxSecondaryLocations);
			}
			return 0;
		},
		mapFallbackImage(): string {
			switch (this.topLevelDomain) {
				case 'de':
					// eslint-disable-next-line global-require
					return require('../../assets/images/gmap_germany.png');
				case 'ch':
					// eslint-disable-next-line global-require
					return require('../../assets/images/gmap_switzerland.png');
				case 'at':
				default:
					// eslint-disable-next-line global-require
					return require('../../assets/images/gmap_austria.png');
			}
		}
	},
	mounted() {
		if (!this.$isMobile.any) {
			this.registerIntersectionObserver();
		}
	},
	props: {
		company: {
			type: Object as PropType<IAPICompany>,
			required: true,
			default: () => ({})
		},
		additionalData: {
			type: Object as PropType<IAdditionalData>,
			required: false
		},
		editMode: {
			type: Boolean,
			default: false
		},
		displayCTAButtons: {
			type: Boolean,
			default: true
		}
	}
});
